import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { navigation, contactInfo } from '../../data';

const Navbar = ({ solid = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine if navbar should be solid
  const isSolid = isScrolled || solid;

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  return (
    <>
      <nav 
        className={`fixed w-full z-50 transition-all duration-500 ${
          isSolid ? 'bg-[var(--brand)] shadow-lg py-2' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/f8d1e252-a8ac-4976-b227-83d6c75463a6.png" 
                  alt="Golf4Life Logo" 
                  className="h-14 md:h-20 w-auto transition-all duration-300"
                />
              </Link>
            </div>
            
            {/* Menu button (Always visible) */}
            <div className="flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                className="p-2 transition-all hover:scale-110 active:scale-95 text-white"
              >
                <Menu size={32} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Fullscreen Sidebar Menu */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[60]">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute top-0 left-0 bottom-0 w-full max-w-md md:max-w-xl bg-[var(--brand)] shadow-2xl flex flex-col"
            >
              <div className="p-8 flex justify-between items-center border-b border-white/10">
                <div className="flex items-center">
                  <img 
                    src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/f8d1e252-a8ac-4976-b227-83d6c75463a6.png" 
                    alt="Golf4Life Logo" 
                    className="h-20 w-auto"
                  />
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-white transition-all hover:rotate-90 hover:scale-110 active:scale-95"
                >
                  <X size={32} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-12 px-8 md:px-16">
                <nav className="space-y-4">
                  {navigation.map((item, index) => {
                    const isExternal = item.href.startsWith('http');
                    const isHash = item.href.includes('#');
                    
                    if (isHash && !isExternal) {
                      return (
                        <motion.a
                          key={item.name}
                          href={item.href}
                          initial={{ opacity: 0, x: -30 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + index * 0.1 }}
                          className="flex items-center justify-between p-4 text-2xl md:text-3xl font-medium uppercase tracking-tighter text-white hover:text-white/70 hover:translate-x-4 transition-all group"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.name}
                          <ArrowRight size={24} className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-white/70" />
                        </motion.a>
                      );
                    }

                    return (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                      >
                        <Link
                          to={item.href}
                          className="flex items-center justify-between p-4 text-2xl md:text-3xl font-medium uppercase tracking-tighter text-white hover:text-white/70 hover:translate-x-4 transition-all group"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.name}
                          <ArrowRight size={24} className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-white/70" />
                        </Link>
                      </motion.div>
                    );
                  })}
                </nav>
              </div>

              <div className="p-8 md:p-16 border-t border-white/10 bg-black/10">
                <p className="text-sm font-medium uppercase tracking-widest text-white/40 mb-6">Kontakt os direkte</p>
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center justify-center gap-4 p-6 bg-[#39d14b] text-white rounded-full text-xl font-medium uppercase tracking-wider hover:-translate-y-1 transition-all shadow-xl shadow-black/20"
                >
                  <Phone size={24} />
                  {contactInfo.phone}
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </>
  );
};

export default Navbar;
